/**
 * Update Policy Provider
 * Manages Firestore listeners for update policies and provides context to the app
 * IMPORTANT: This provider should only be used in authenticated routes
 * as it requires Firebase Auth to read Firestore documents
 */

import React, { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import FirestoreUpdatesService from 'src/utils/Services/firestore-updates.service';

import { useSystemStore } from 'src/store/SystemStore';
import { getRuntime, hasCapability } from 'src/platform';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import useAuthStore from 'src/auth/store/firebase/auth-store';
import { useConnectivityStore } from 'src/features/connectivity/connectivity-store';

import type {
  UpdateCheckResult,
  UpdatePolicyState,
  GlobalUpdatePolicy,
  BusinessUpdatePolicy,
} from 'src/types/update-policy';

interface UpdatePolicyContextValue {
  policyState: UpdatePolicyState;
  checkUpdateRequired: (currentVersion: string) => Promise<UpdateCheckResult>;
  refreshPolicy: () => Promise<void>;
}

const UpdatePolicyContext = createContext<UpdatePolicyContextValue | null>(null);

export const useUpdatePolicy = () => {
  const context = useContext(UpdatePolicyContext);
  if (!context) {
    throw new Error('useUpdatePolicy must be used within UpdatePolicyProvider');
  }
  return context;
};

interface UpdatePolicyProviderProps {
  children: React.ReactNode;
}

export function UpdatePolicyProvider({ children }: UpdatePolicyProviderProps) {
  // Mobile apps only: web has no update policy, and the Windows POS updates
  // through Intune with its own release version (never Android's policy).
  const remoteVersionPolicy = hasCapability('remoteVersionPolicy');
  const platform = getRuntime().kind;

  // Use the auth store's authenticated state
  // IMPORTANT: This ensures we only start Firestore listeners AFTER web SDK auth is synced
  // (The auth store sets authenticated=true only after syncing web SDK auth on native platforms)
  const { authenticated: isAuthenticated, authInitialized: authChecked } = useAuthStore();
  const isOnline = useConnectivityStore((state) => state.runtimeState === 'ONLINE');

  // Get business ID from user data store (populated in PreloadDataGuard)
  const business = useDeviceSyncStore((state) => state.businessData);
  const businessId = business?.id;

  const {
    updatePolicy,
    setUpdatePolicy,
    setGlobalPolicy,
    setBusinessPolicy,
    setMergedPolicy,
    setUpdatePolicyLoading,
    setUpdatePolicyError,
  } = useSystemStore();

  const [policyState, setPolicyState] = useState<UpdatePolicyState>(() => ({
    globalPolicy: updatePolicy.globalPolicy,
    businessPolicy: updatePolicy.businessPolicy,
    mergedPolicy: updatePolicy.mergedPolicy,
    // An offline remount must never wait for a Firestore snapshot. Reuse any
    // policy already checked in this application lifetime and refresh on reconnect.
    loading: Boolean(remoteVersionPolicy && isOnline && !updatePolicy.lastFetch),
    error: updatePolicy.error,
    lastFetch: updatePolicy.lastFetch,
  }));

  /**
   * Handle global policy update
   */
  const handleGlobalPolicyUpdate = useCallback(
    (policy: GlobalUpdatePolicy | null) => {
      setPolicyState((prev) => {
        const newState = {
          ...prev,
          globalPolicy: policy,
          lastFetch: new Date(),
        };

        // Merge policies
        const merged = FirestoreUpdatesService.merge(policy, prev.businessPolicy);
        newState.mergedPolicy = merged;

        return newState;
      });

      // Update store
      setGlobalPolicy(policy);
      setUpdatePolicy({
        globalPolicy: policy,
        lastFetch: new Date(),
      });
    },
    [setGlobalPolicy, setUpdatePolicy]
  );

  /**
   * Handle business policy update
   */
  const handleBusinessPolicyUpdate = useCallback(
    (policy: BusinessUpdatePolicy | null) => {
      setPolicyState((prev) => {
        const newState = {
          ...prev,
          businessPolicy: policy,
          lastFetch: new Date(),
        };

        // Merge policies
        const merged = FirestoreUpdatesService.merge(prev.globalPolicy, policy);
        newState.mergedPolicy = merged;

        return newState;
      });

      // Update store
      setBusinessPolicy(policy);
      setUpdatePolicy({
        businessPolicy: policy,
        lastFetch: new Date(),
      });
    },
    [setBusinessPolicy, setUpdatePolicy]
  );

  /**
   * Handle errors
   */
  const handleError = useCallback(
    (error: Error) => {
      const errorMessage = error.message || 'Failed to fetch update policy';
      setPolicyState((prev) => ({
        ...prev,
        error: errorMessage,
        loading: false,
      }));
      setUpdatePolicyError(errorMessage);
      setUpdatePolicyLoading(false);
    },
    [setUpdatePolicyError, setUpdatePolicyLoading]
  );

  // NOTE: Authentication state is now managed by useAuthStore
  // This ensures Firestore listeners only start AFTER web SDK auth is synced on native platforms
  // (Previously we listened to native auth changes directly, causing race conditions)

  /**
   * Set up Firestore listeners
   * IMPORTANT: Only runs when user is authenticated
   */
  useEffect(() => {
    // Skip Firestore listeners on non-native platforms
    if (!remoteVersionPolicy) {
      console.log(
        `[UpdatePolicyProvider] Platform ${platform} detected - skipping update policy listeners`
      );
      setUpdatePolicyLoading(false);
      // Return empty cleanup function for consistency
      return () => {};
    }

    // The connectivity coordinator is authoritative for whether PointCraft can
    // perform online work. Do not let Firestore's native transport timeout hold
    // the offline shell on a splash screen.
    if (!isOnline) {
      setPolicyState((prev) => ({ ...prev, loading: false }));
      setUpdatePolicyLoading(false);
      return () => {};
    }

    // Wait for auth check to complete
    if (!authChecked) {
      console.log('[UpdatePolicyProvider] Waiting for auth check to complete');
      return () => {};
    }
    console.log('[UpdatePolicyProvider] Auth check complete');

    // Only set up Firestore listeners if authenticated
    if (!isAuthenticated) {
      console.log('[UpdatePolicyProvider] User not authenticated - skipping Firestore listeners');
      setPolicyState((prev) => ({ ...prev, loading: false }));
      setUpdatePolicyLoading(false);
      return () => {};
    }

    console.log('[UpdatePolicyProvider] User authenticated - setting up Firestore listeners');

    // The initial online mount starts in loading state. Reconnect refreshes are
    // background work when a cached result has already allowed the shell through.
    setUpdatePolicyLoading(true);

    // Listen to global policy
    console.warn('[UpdatePolicyProvider] Setting up global policy listener');
    const globalUnsubscribe = FirestoreUpdatesService.listenToGlobalPolicy(
      (policy) => {
        console.warn('[UpdatePolicyProvider] Global policy received:', !!policy);
        handleGlobalPolicyUpdate(policy);
        setPolicyState((prev) => ({ ...prev, loading: false, error: null }));
        setUpdatePolicyLoading(false);
        setUpdatePolicyError(null);
      },
      (error) => {
        console.error('[UpdatePolicyProvider] Global policy error:', error);
        handleError(error);
      }
    );

    // Listen to business policy if businessId is available
    let businessUnsubscribe: (() => void) | null = null;
    if (businessId) {
      businessUnsubscribe = FirestoreUpdatesService.listenToBusinessPolicy(
        businessId,
        (policy) => {
          handleBusinessPolicyUpdate(policy);
        },
        handleError
      );
    }

    // Cleanup on unmount or businessId change
    return () => {
      if (globalUnsubscribe) {
        globalUnsubscribe();
      }
      if (businessUnsubscribe) {
        businessUnsubscribe();
      }
    };
  }, [
    businessId,
    handleGlobalPolicyUpdate,
    handleBusinessPolicyUpdate,
    handleError,
    setUpdatePolicyLoading,
    setUpdatePolicyError,
    remoteVersionPolicy,
    isOnline,
    platform,
    authChecked,
    isAuthenticated,
  ]);

  /**
   * Update merged policy in store when state changes
   */
  useEffect(() => {
    if (policyState.mergedPolicy) {
      setMergedPolicy(policyState.mergedPolicy);
    }
  }, [policyState.mergedPolicy, setMergedPolicy]);

  /**
   * Check if update is required
   */
  const checkUpdateRequired = useCallback(
    async (currentVersion: string): Promise<UpdateCheckResult> => {
      // No update checks on non-native platforms
      if (!remoteVersionPolicy) {
        return {
          updateRequired: false,
          updateType: 'none',
          currentVersion,
          requiredVersion: null,
          isBlocked: false,
          availableInCapacitor: false,
        };
      }

      if (!policyState.mergedPolicy) {
        return {
          updateRequired: false,
          updateType: 'none',
          currentVersion,
          requiredVersion: null,
          isBlocked: false,
          availableInCapacitor: false,
        };
      }

      return FirestoreUpdatesService.checkUpdateRequired(currentVersion, policyState.mergedPolicy);
    },
    [policyState.mergedPolicy, remoteVersionPolicy]
  );

  /**
   * Manually refresh policies
   */
  const refreshPolicy = useCallback(async () => {
    // Skip on non-native platforms
    if (!remoteVersionPolicy) {
      console.log('Skipping policy refresh on non-native platform');
      return;
    }

    setPolicyState((prev) => ({ ...prev, loading: true }));
    setUpdatePolicyLoading(true);

    try {
      // Fetch policies once
      const [globalPolicy, businessPolicy] = await Promise.all([
        FirestoreUpdatesService.getGlobalPolicy(),
        businessId ? FirestoreUpdatesService.getBusinessPolicy(businessId) : Promise.resolve(null),
      ]);

      handleGlobalPolicyUpdate(globalPolicy);
      if (businessPolicy) {
        handleBusinessPolicyUpdate(businessPolicy);
      }

      setPolicyState((prev) => ({
        ...prev,
        loading: false,
        error: null,
        lastFetch: new Date(),
      }));
      setUpdatePolicyLoading(false);
      setUpdatePolicyError(null);
    } catch (error) {
      handleError(error as Error);
    }
  }, [
    businessId,
    handleGlobalPolicyUpdate,
    handleBusinessPolicyUpdate,
    handleError,
    setUpdatePolicyLoading,
    setUpdatePolicyError,
    remoteVersionPolicy,
  ]);

  const contextValue: UpdatePolicyContextValue = useMemo(
    () => ({
      policyState,
      checkUpdateRequired,
      refreshPolicy,
    }),
    [policyState, checkUpdateRequired, refreshPolicy]
  );

  return (
    <UpdatePolicyContext.Provider value={contextValue}>{children}</UpdatePolicyContext.Provider>
  );
}

export default UpdatePolicyProvider;
